/**
 * Retirement Panel — modal listing stale/expired active flags with bulk archive.
 */

import { useMemo, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { useI18n } from '@/i18n';
import { parseBackendDateTime } from '@/lib/format/datetime';
import type { FlagResponse } from '@/types/featureFlags';
import { getExpiryInfo, getStaleFlags } from '../utils/stale';
import { useTransitionFlag } from '../hooks/useFeatureFlags';

interface RetirementPanelProps {
  flags: FlagResponse[];
  onClose: () => void;
}

export function RetirementPanel({ flags, onClose }: RetirementPanelProps) {
  const { t, language } = useI18n();
  const transitionMutation = useTransitionFlag();
  const staleFlags = useMemo(() => getStaleFlags(flags), [flags]);
  const [selected, setSelected] = useState<string[]>([]);

  const allSelected = staleFlags.length > 0 && selected.length === staleFlags.length;

  const toggle = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const toggleAll = () =>
    setSelected(allSelected ? [] : staleFlags.map((f) => f.flag_key));

  const formatDate = (iso?: string) =>
    iso
      ? new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(parseBackendDateTime(iso))
      : '';

  const handleArchive = async () => {
    let archived = 0;
    for (const key of selected) {
      try {
        await transitionMutation.mutateAsync({ flagKey: key, data: { new_state: 'archived' } });
        archived += 1;
      } catch {
        // per-flag failure already toasted by mutation onError
      }
    }
    if (archived > 0) {
      toast.success(t('featureFlags.retirement.archiveSuccess', { count: String(archived) }));
    }
    setSelected([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('featureFlags.retirement.title')}</h2>
              <p className="text-sm text-gray-500">{t('featureFlags.retirement.description')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {staleFlags.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">{t('featureFlags.retirement.noStale')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                <Checkbox checked={allSelected} onChange={toggleAll} label={t('featureFlags.retirement.selectAll')} />
              </div>
              {staleFlags.map((flag) => {
                const { status } = getExpiryInfo(flag.expires_at);
                return (
                  <div
                    key={flag.flag_key}
                    className="flex items-center gap-3 px-3 py-2.5 border border-gray-200 rounded-lg"
                  >
                    <Checkbox
                      checked={selected.includes(flag.flag_key)}
                      onChange={() => toggle(flag.flag_key)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-medium text-gray-900 truncate">
                          {flag.flag_key}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${
                            status === 'expired'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {status === 'expired'
                            ? t('featureFlags.retirement.expired')
                            : t('featureFlags.retirement.expiringSoon')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {flag.owner || t('featureFlags.retirement.noOwner')} · {formatDate(flag.expires_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-6 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={handleArchive}
            disabled={selected.length === 0}
            loading={transitionMutation.isPending}
          >
            {t('featureFlags.retirement.archiveSelected')}
          </Button>
        </div>
      </div>
    </div>
  );
}
